import {
    useQuery,
    useQueryClient,
    useMutation,
    useInfiniteQuery,
    keepPreviousData,
    type QueryClient,
} from "@tanstack/react-query";
import axios from "axios";
import { ClientInferResponseBody } from "@ts-rest/core";
import { ruleContract } from "@common/contracts/rule.contract";
import {
    RuleConditionType,
    RuleGroupType,
    RuleSource,
    RuleConditionAmount,
    RuleConditionAmountRounding,
    TransactionRuleGroup,
    TransactionRule,
    TransactionRuleActionDefinition,
    TransactionRuleConditionDefinition,
    RuleConditionDate,
    RuleConditionSimilarTo,
    TransferRuleDirection,
} from "@common/types/transactionRule";
import {
    JobProgressEvent,
    JobProgressStatus,
} from "@common/types/job-progress";
import { TransactionListItem } from "@common/schemas/transaction.schema";
import type {
    TransactionRuleReviewer,
    TransactionRuleReviewWithReviewer,
} from "@common/schemas/transactionRuleReview.schema";
import { TransactionRuleGroupDto } from "@common/dto/transactionRule/transaction-rule-group.dto";
import { TransactionRuleDto } from "@common/dto/transactionRule/transaction-rule.dto";
import { Counterparty } from "@common/types/counterparty";
import { useScope } from "@state/scopeContext.lib";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import { useNatsSubscription } from "@hooks/useNatsSubscription";
import { getBackendAPIClient } from "@lib/backendAPIClient";
import { SUPPRESS_ERROR_TOAST_HEADER } from "@lib/backendClient";
import {
    getMatchingTransactionsForRule,
    getAppliedTransactionsForRule,
} from "@lib/transactionRules";
import { getAppliedTransfersForRule } from "@lib/transferRules";
import { hasSameRuleContent } from "@common/helpers/ruleContent";
import { queryClient } from "../queryClient";

export const getRuleGroupsQueryKey = (workspaceId: string) => [
    "rule-groups",
    workspaceId,
];

export const rulesContractClient = getBackendAPIClient(ruleContract);

export function getRuleApplicationQueryKeys(workspaceId: string) {
    return [
        getRuleGroupsQueryKey(workspaceId),
        ["default-accounting-rules", workspaceId],
        ["rulesAppliedTransactions", workspaceId],
    ];
}

export async function invalidateRuleApplicationQueries(
    workspaceId: string,
    qc: QueryClient = queryClient,
): Promise<void> {
    await Promise.all(
        getRuleApplicationQueryKeys(workspaceId).map((queryKey) =>
            qc.invalidateQueries({ queryKey }),
        ),
    );
}

function useRuleGroupsQueryOptions(options?: { enabled?: boolean }) {
    const { workspaceId } = useScope();

    return {
        queryKey: getRuleGroupsQueryKey(workspaceId ?? ""),
        queryFn: async () => {
            if (!workspaceId) {
                return undefined;
            }

            const response = await rulesContractClient.getRuleGroups({
                params: {
                    workspaceId: workspaceId ?? "",
                },
            });

            return {
                ...response.body,
                ruleGroups: response.body.ruleGroups.map((group) =>
                    TransactionRuleGroupDto.deserialize(group),
                ),
            };
        },
        enabled: options?.enabled ?? true,
        staleTime: 2 * 60 * 1000,
    };
}

export function useWorkspaceRuleGroups(options?: { enabled?: boolean }) {
    return useQuery(useRuleGroupsQueryOptions(options));
}

export function useWorkspaceRuleGroupsList() {
    return useQuery({
        ...useRuleGroupsQueryOptions(),
        select: (data) => data?.ruleGroups,
    });
}

export function useRuleTransactionCounts() {
    return useQuery({
        ...useRuleGroupsQueryOptions(),
        select: (data) => data?.transactionCounts,
    });
}

export function useRuleReviews(ruleId: string) {
    return useQuery({
        ...useRuleGroupsQueryOptions(),
        select: (data) => data?.ruleReviews?.[ruleId],
    });
}

export function useDefaultAccountingRules(options?: { enabled?: boolean }) {
    const { workspaceId } = useScope();

    return useQuery({
        queryKey: ["default-accounting-rules", workspaceId],
        queryFn: async () => {
            if (!workspaceId) {
                return undefined;
            }

            const response =
                await rulesContractClient.getAccountingDefaultRules({
                    params: {
                        workspaceId: workspaceId ?? "",
                    },
                });

            return {
                ruleGroup: TransactionRuleGroupDto.deserialize(
                    response.body.ruleGroup,
                ),
                transactionCounts: response.body.transactionCounts,
            };
        },
        enabled: options?.enabled ?? true,
        staleTime: 2 * 60 * 1000,
    });
}

interface UpdateRulePositionParams {
    workspaceId: string;
    rule: TransactionRule;
}

const moveRule = (
    ruleGroups: TransactionRuleGroup[],
    movedRule: TransactionRule,
) =>
    ruleGroups.map((group) => {
        // we are moving the rule here
        if (group.id === movedRule.groupId) {
            const newRules = (group.rules ?? []).map((rule) => {
                if (rule.id === movedRule.id) {
                    return {
                        ...rule,
                        order: movedRule.order,
                    };
                }
                return rule;
            });

            // if it is moved to a new group, we need to add it manually
            if (newRules.every((rule) => rule.id !== movedRule.id)) {
                newRules.push(movedRule);
            }

            return {
                ...group,
                rules: newRules.toSorted((a, b) => a.order - b.order),
            };
        }
        // we remove from any other group it might be at
        return {
            ...group,
            rules: group.rules?.filter((rule) => rule.id !== movedRule.id),
        };
    });

export const useUpdateRulePosition = () => {
    const { workspaceId } = useScope();
    const queryKey = getRuleGroupsQueryKey(workspaceId ?? "");
    const { isViewOnly } = useViewOnlyRefusal();

    return useMutation({
        mutationFn: async ({ rule }: UpdateRulePositionParams) => {
            const response = await rulesContractClient.changeRuleOrder({
                params: {
                    workspaceId: workspaceId ?? "",
                    ruleId: rule.id,
                },
                body: {
                    groupId: rule.groupId,
                    order: rule.order,
                },
            });

            return TransactionRuleDto.deserialize(response.body.rule);
        },
        onMutate: async ({ rule }: UpdateRulePositionParams) => {
            // View-only is refused by the API; an optimistic write would only flash.
            if (isViewOnly) {
                return;
            }

            await queryClient.cancelQueries({ queryKey });

            const previousRuleGroups = queryClient.getQueryData<{
                ruleGroups: TransactionRuleGroup[];
            }>(queryKey);

            const updatedRuleGroups = moveRule(
                previousRuleGroups?.ruleGroups ?? [],
                rule,
            );

            queryClient.setQueryData(queryKey, {
                ...previousRuleGroups,
                ruleGroups: updatedRuleGroups,
            });

            return { previousRuleGroups };
        },
        onError: (_, __, context) => {
            // We need to move the rule back to its previous position. Bail
            // without a snapshot: the previous `?? []` wrote an empty array
            // over the `{ ruleGroups }` shape, emptying the list instead of
            // restoring it, which is what a skipped optimistic write hits.
            if (!context?.previousRuleGroups) {
                return;
            }

            queryClient.setQueryData(queryKey, context.previousRuleGroups);
        },
    });
};

interface RuleData {
    categoryId?: string | null;
    entityId?: number | null;
    classId?: string | null;
    accountId?: string | null;
    isDisabled?: boolean;
    order?: number;
    groupId?: string;
    conditions?: Array<{
        conditionType: RuleConditionType;
        value:
            | string
            | string[]
            | number
            | number[]
            | RuleConditionAmount
            | RuleConditionDate
            | RuleConditionAmountRounding
            | RuleConditionSimilarTo;
        transferDirection: TransferRuleDirection | null;
    }>;
    actions?: TransactionRuleActionDefinition[];
    note?: string | null;
}

interface CreateRuleParams {
    workspaceId: string;
    rule: RuleData;
    source: RuleSource;
    applyToExistingTransactions: boolean;
    excludedTransactionsIds?: number[];
    includedTransactionsIds?: number[];
    excludeMatchedTransfers?: boolean;
    suppressErrorToast?: boolean;
}

export const useCreateRule = (onSuccess?: () => void) =>
    useMutation({
        mutationFn: async ({
            workspaceId,
            rule,
            source,
            applyToExistingTransactions,
            excludedTransactionsIds = [],
            includedTransactionsIds = [],
            excludeMatchedTransfers,
            suppressErrorToast,
        }: CreateRuleParams) => {
            const response = await rulesContractClient.createRule({
                params: {
                    workspaceId,
                },
                body: {
                    rule,
                    source,
                    applyToExistingTransactions,
                    excludedTransactionsIds,
                    includedTransactionsIds,
                    excludeMatchedTransfers,
                },
                ...(suppressErrorToast
                    ? {
                          extraHeaders: {
                              [SUPPRESS_ERROR_TOAST_HEADER]: "true",
                          },
                      }
                    : {}),
            });

            return TransactionRuleDto.deserialize(response.body.rule);
        },
        onSuccess: (data, variables) => {
            // Update the query cache with the new rule
            const queryKey = getRuleGroupsQueryKey(variables.workspaceId);
            const currentData = queryClient.getQueryData<{
                ruleGroups: TransactionRuleGroup[];
                counterparties: Counterparty[];
                transactionCounts: Record<string, number>;
                matchingCounts: Record<string, number>;
            }>(queryKey);

            if (currentData) {
                const updatedRuleGroups = currentData.ruleGroups.map(
                    (group) => {
                        if (group.id === variables.rule.groupId) {
                            return {
                                ...group,
                                rules: [...(group.rules ?? []), data],
                            };
                        }
                        return group;
                    },
                );

                queryClient.setQueryData(queryKey, {
                    ...currentData,
                    ruleGroups: updatedRuleGroups,
                });
            }
            // Actions-only edits don't change the ruleId, so invalidate the
            // matching-transactions caches to refresh the preview total.
            void queryClient.invalidateQueries({
                queryKey: ["rulesMatchingTransactions"],
            });
            void queryClient.invalidateQueries({
                queryKey: ["ruleMatchingTransactionsDrawer"],
            });
            onSuccess?.();
        },
    });

interface UpdateRuleParams extends Omit<CreateRuleParams, "source"> {
    ruleId: string;
}

export const useUpdateRule = (onSuccess?: () => void) =>
    useMutation({
        mutationFn: async ({
            workspaceId,
            ruleId,
            rule,
            applyToExistingTransactions,
            excludedTransactionsIds = [],
            includedTransactionsIds = [],
            suppressErrorToast,
        }: UpdateRuleParams) => {
            const response = await rulesContractClient.updateRule({
                params: {
                    workspaceId,
                    ruleId,
                },
                body: {
                    rule,
                    applyToExistingTransactions,
                    excludedTransactionsIds,
                    includedTransactionsIds,
                },
                ...(suppressErrorToast
                    ? {
                          extraHeaders: {
                              [SUPPRESS_ERROR_TOAST_HEADER]: "true",
                          },
                      }
                    : {}),
            });

            return TransactionRuleDto.deserialize(response.body.rule);
        },
        onSuccess: (data, variables) => {
            const queryKey = getRuleGroupsQueryKey(variables.workspaceId);
            const currentData = queryClient.getQueryData<{
                ruleGroups: TransactionRuleGroup[];
                ruleReviews?: Record<
                    string,
                    TransactionRuleReviewWithReviewer[]
                >;
            }>(queryKey);

            if (currentData) {
                const previousRule = currentData.ruleGroups
                    .flatMap((group) => group.rules ?? [])
                    .find((rule) => rule.id === variables.ruleId);
                const targetGroupId = variables.rule.groupId ?? data.groupId;
                const updatedRuleGroups = currentData.ruleGroups.map(
                    (group) => {
                        if (group.id === targetGroupId) {
                            return {
                                ...group,
                                rules: (group.rules ?? []).map((rule) =>
                                    rule.id === variables.ruleId ? data : rule,
                                ),
                            };
                        }
                        return group;
                    },
                );
                const isContentUnchanged =
                    previousRule !== undefined &&
                    hasSameRuleContent(previousRule, data);

                queryClient.setQueryData(queryKey, {
                    ...currentData,
                    ruleGroups: updatedRuleGroups,
                    ruleReviews: isContentUnchanged
                        ? currentData.ruleReviews
                        : Object.fromEntries(
                              Object.entries(
                                  currentData.ruleReviews ?? {},
                              ).filter(
                                  ([reviewedRuleId]) =>
                                      reviewedRuleId !== variables.ruleId,
                              ),
                          ),
                });
            }

            // Actions-only edits don't change the ruleId, so invalidate the
            // matching-transactions caches to refresh the preview total.
            void queryClient.invalidateQueries({
                queryKey: ["rulesMatchingTransactions"],
            });
            void queryClient.invalidateQueries({
                queryKey: ["ruleMatchingTransactionsDrawer"],
            });
            onSuccess?.();
        },
    });

interface UpdateRuleNoteParams {
    workspaceId: string;
    ruleId: string;
    note: string | null;
}

export const useUpdateRuleNote = () =>
    useMutation({
        mutationFn: async ({
            workspaceId,
            ruleId,
            note,
        }: UpdateRuleNoteParams) => {
            const response = await rulesContractClient.updateRuleNote({
                params: {
                    workspaceId,
                    ruleId,
                },
                body: {
                    note,
                },
            });

            return TransactionRuleDto.deserialize(response.body.rule);
        },
        onSuccess: (data, variables) => {
            const queryKey = getRuleGroupsQueryKey(variables.workspaceId);
            const currentData = queryClient.getQueryData<{
                ruleGroups: TransactionRuleGroup[];
            }>(queryKey);

            if (!currentData) {
                return;
            }

            const updatedRuleGroups = currentData.ruleGroups.map((group) => ({
                ...group,
                rules: (group.rules ?? []).map((rule) =>
                    rule.id === variables.ruleId ? data : rule,
                ),
            }));

            queryClient.setQueryData(queryKey, {
                ...currentData,
                ruleGroups: updatedRuleGroups,
            });
        },
    });

interface RuleReviewParams {
    workspaceId: string;
    ruleId: string;
    userId: number;
}

interface AddRuleReviewParams extends RuleReviewParams {
    reviewer: TransactionRuleReviewer;
}

function setRuleReviewsInCache({
    workspaceId,
    ruleId,
    update,
}: {
    workspaceId: string;
    ruleId: string;
    update: (
        reviews: TransactionRuleReviewWithReviewer[],
    ) => TransactionRuleReviewWithReviewer[];
}) {
    queryClient.setQueryData<
        ClientInferResponseBody<typeof ruleContract.getRuleGroups, 200>
    >(getRuleGroupsQueryKey(workspaceId), (currentData) =>
        currentData
            ? {
                  ...currentData,
                  ruleReviews: {
                      ...currentData.ruleReviews,
                      [ruleId]: update(currentData.ruleReviews?.[ruleId] ?? []),
                  },
              }
            : currentData,
    );
}

export const useAddRuleReview = () =>
    useMutation({
        mutationFn: async ({ workspaceId, ruleId }: AddRuleReviewParams) => {
            const response = await rulesContractClient.addRuleReview({
                params: { workspaceId, ruleId },
            });
            if (response.status !== 200) {
                throw new Error("Failed to mark rule as reviewed");
            }
            return response.body.review;
        },
        onSuccess: (review, { workspaceId, ruleId, userId, reviewer }) => {
            setRuleReviewsInCache({
                workspaceId,
                ruleId,
                update: (reviews) => [
                    ...reviews.filter((existing) => existing.userId !== userId),
                    { ...review, reviewer },
                ],
            });
        },
    });

export const useRevokeRuleReview = () =>
    useMutation({
        mutationFn: async ({ workspaceId, ruleId }: RuleReviewParams) => {
            const response = await rulesContractClient.revokeRuleReview({
                params: { workspaceId, ruleId },
            });
            if (response.status !== 200) {
                throw new Error("Failed to mark rule as unreviewed");
            }
            return response.body;
        },
        onSuccess: (_, { workspaceId, ruleId, userId }) => {
            setRuleReviewsInCache({
                workspaceId,
                ruleId,
                update: (reviews) =>
                    reviews.filter((existing) => existing.userId !== userId),
            });
        },
    });

interface RuleRemovalOptions {
    onSuccess?: () => void;
    onError?: (error: RuleRemovalError) => void;
}

type RuleRemovalError = ClientInferResponseBody<
    typeof ruleContract.deleteRule,
    400
>;

export function useRuleRemovalMutation(
    rule: TransactionRule,
    { onSuccess, onError }: RuleRemovalOptions = {},
) {
    return useMutation({
        mutationFn: () =>
            rulesContractClient.deleteRule({
                params: {
                    workspaceId: rule.workspaceId,
                    ruleId: rule.id,
                },
            }),

        onSuccess: () => {
            const queryKey = getRuleGroupsQueryKey(rule.workspaceId);
            const currentData = queryClient.getQueryData<{
                ruleGroups: TransactionRuleGroup[];
            }>(queryKey);

            if (currentData) {
                const updatedRuleGroups = currentData.ruleGroups.map(
                    (group) => {
                        if (group.id === rule.groupId) {
                            return {
                                ...group,
                                rules: (group.rules ?? []).filter(
                                    (r) => r.id !== rule.id,
                                ),
                            };
                        }
                        return group;
                    },
                );

                queryClient.setQueryData(queryKey, {
                    ...currentData,
                    ruleGroups: updatedRuleGroups,
                });
            }
            onSuccess?.();
        },
        onError: (error) => {
            if (
                axios.isAxiosError(error) &&
                error.response?.status === 400 &&
                typeof error.response.data === "object" &&
                !("message" in error.response.data)
            ) {
                onError?.(error.response.data as RuleRemovalError);
            }
        },
    });
}

interface MatchingRuleQueryParams {
    ruleId?: string;
    conditions?: TransactionRuleConditionDefinition[];
    groupType?: RuleGroupType;
    page: number;
    limit: number;
    enabled: boolean;
}

export function useMatchingTransactionsForRuleQuery({
    ruleId,
    conditions,
    groupType,
    page,
    limit,
    enabled,
}: MatchingRuleQueryParams) {
    const { workspaceId } = useScope();

    return useQuery({
        queryKey: [
            "ruleMatchingTransactionsDrawer",
            workspaceId,
            ruleId,
            conditions,
            groupType,
            page,
        ],
        queryFn: async () => {
            if (!workspaceId || !groupType || !conditions) {
                return { data: [] as TransactionListItem[] };
            }
            const result = await getMatchingTransactionsForRule(workspaceId, {
                groupType,
                conditions,
                page,
                limit,
                // Saved-rule drawer: BE derives lock-safety from the rule.
                ruleId,
            });
            return { data: result.transactions, total: result.total };
        },
        enabled: enabled && !!workspaceId,
        placeholderData: keepPreviousData,
    });
}

interface MatchingTransactionsForRuleInfiniteQueryParams {
    groupType: RuleGroupType;
    conditions: TransactionRuleConditionDefinition[];
    ruleId?: string;
    actions?: Parameters<typeof getMatchingTransactionsForRule>[1]["actions"];
    splitActionConfig?: Parameters<
        typeof getMatchingTransactionsForRule
    >[1]["splitActionConfig"];
    excludeSplitTransactions?: boolean;
    enabled: boolean;
    pageSize?: number;
}

const DEFAULT_PREVIEW_PAGE_SIZE = 50;

export function useMatchingTransactionsForRuleInfiniteQuery({
    groupType,
    conditions,
    ruleId,
    actions,
    splitActionConfig,
    excludeSplitTransactions,
    enabled,
    pageSize = DEFAULT_PREVIEW_PAGE_SIZE,
}: MatchingTransactionsForRuleInfiniteQueryParams) {
    const { workspaceId } = useScope();

    return useInfiniteQuery({
        queryKey: [
            "rulesMatchingTransactions",
            workspaceId,
            groupType,
            conditions,
            ruleId,
            // Re-key on actions so memo-only ↔ category+memo refetches a new
            // total (lock-safety changes which rows are counted).
            actions,
            splitActionConfig,
            excludeSplitTransactions,
        ],
        queryFn: async ({ pageParam = 0 }) => {
            if (!workspaceId) {
                return undefined;
            }

            return await getMatchingTransactionsForRule(workspaceId, {
                groupType,
                conditions,
                // Draft actions drive lock-safety; ruleId is the fallback the BE
                // uses (saved rule) when no draft actions are sent.
                actions,
                ruleId,
                splitActionConfig,
                excludeSplitTransactions,
                page: pageParam,
                limit: pageSize,
            });
        },
        enabled: enabled && !!workspaceId,
        initialPageParam: 0,
        getNextPageParam: (lastPage, pages) =>
            lastPage?.transactions.length === pageSize
                ? pages.length
                : undefined,
    });
}

interface AppliedTransferTransactionsQueryParams {
    ruleId?: string;
    enabled: boolean;
    pageSize?: number;
}

export function useAppliedTransferTransactionsInfiniteQuery({
    ruleId,
    enabled,
    pageSize = 50,
}: AppliedTransferTransactionsQueryParams) {
    const { workspaceId } = useScope();

    return useInfiniteQuery({
        queryKey: [
            "rulesAppliedTransferTransactions",
            workspaceId,
            ruleId,
            pageSize,
        ],
        queryFn: async ({ pageParam = 0 }) => {
            if (!workspaceId || !ruleId) {
                return;
            }

            return await getAppliedTransfersForRule(workspaceId, ruleId, {
                page: pageParam,
                limit: pageSize,
            });
        },
        enabled: enabled && !!workspaceId && !!ruleId,
        initialPageParam: 0,
        getNextPageParam: (lastPage, pages) => {
            if (!lastPage) {
                return undefined;
            }
            const itemsOnPage =
                lastPage.mode === "allow"
                    ? lastPage.matches.length
                    : lastPage.transactions.length;
            return itemsOnPage === pageSize ? pages.length : undefined;
        },
    });
}

interface AppliedTransactionsQueryParams {
    ruleId?: string;
    enabled: boolean;
}

export function useAppliedTransactionsForRuleQuery({
    ruleId,
    enabled,
}: AppliedTransactionsQueryParams) {
    const { workspaceId } = useScope();
    const PAGE_SIZE = 50;

    return useInfiniteQuery({
        queryKey: ["rulesAppliedTransactions", workspaceId, ruleId],
        queryFn: async ({ pageParam = 0 }) => {
            if (!workspaceId || !ruleId) {
                return;
            }

            const result = await getAppliedTransactionsForRule(
                workspaceId,
                ruleId,
                {
                    page: pageParam,
                    limit: PAGE_SIZE,
                },
            );

            return result.transactions;
        },
        enabled: enabled && !!workspaceId,
        initialPageParam: 0,
        getNextPageParam: (lastPage, pages) =>
            lastPage?.length === PAGE_SIZE ? pages.length : undefined,
    });
}

export const RULE_EXECUTION_JOB_TYPE = "rule_execution";

export function getRuleExecutionJobSubject(
    workspaceId: string | null | undefined,
): string | null {
    return workspaceId
        ? `workspaces.${workspaceId}.notifications.jobs.rule-execution`
        : null;
}

export function isCompletedRuleExecutionJob(event: JobProgressEvent): boolean {
    return (
        event.jobType === RULE_EXECUTION_JOB_TYPE &&
        event.status === JobProgressStatus.COMPLETED
    );
}

export function useRuleExecutionJobSubscription() {
    const { workspaceId } = useScope();
    const qc = useQueryClient();

    useNatsSubscription<{ data: JobProgressEvent }>(
        getRuleExecutionJobSubject(workspaceId),
        {
            onMessage: ({ data: jobEvent }) => {
                if (isCompletedRuleExecutionJob(jobEvent)) {
                    if (!workspaceId) {
                        return;
                    }
                    void invalidateRuleApplicationQueries(workspaceId, qc);
                }
            },
        },
    );
}
